import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Check } from 'lucide-react';

function AccessControlPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: "https://erp-back.nyc3.digitaloceanspaces.com/assets_public/no-1.png",
      title: "Usuarios & Grupos",
      description: "La gestión de usuarios y grupos totalmente redefinida y simplificada."
    },
    {
      icon: "https://erp-back.nyc3.digitaloceanspaces.com/assets_public/2.png",
      title: "Traspasa fronteras",
      description: "Crea perfiles y grupos entre varias ubicaciones geográficas, sin importar ciudad o país."
    },
    {
      icon: "https://erp-back.nyc3.digitaloceanspaces.com/assets_public/3.png",
      title: "Developer friendly",
      description: "Administra e integra mediante API a cualquiera de tus plataformas."
    }
  ];

  const capabilities = [
    {
      title: "Un control de acceso Intuitivo",
      image: "https://erp-back.nyc3.digitaloceanspaces.com/assets_public/Captura-de-pantalla-2025-07-23-a-las-3.45.43-p.-m.png",
      features: [
        "Correlaciona eventos de acceso con eventos de video vigilancia y/o triggers de sensores desde un solo lugar",
        "Integra con Entra ID (Azure Active Directory)",
        "Actualizaciones automáticas de seguridad y personalización de flujos de trabajo"
      ]
    },
    {
      title: "Administra por Web & API",
      image: "https://erp-back.nyc3.digitaloceanspaces.com/assets_public/Captura-de-pantalla-2025-07-23-a-las-3.44.40-p.-m.png",
      features: [
        "Crea y administra usuarios directo en consola o vía API",
        "Administra niveles de acceso basado en puertas y/o calendario",
        "Administra de manera simple el acceso a nivel de grupos o perfiles"
      ]
    },
    {
      title: "Acceso sin contacto",
      image: "https://erp-back.nyc3.digitaloceanspaces.com/assets_public/minimize-your-hardware-footprint-1.jpg",
      features: [
        "Acceso mediante aplicación móvil reduciendo inversión en hardware o complementando los métodos tradicionales"
      ]
    },
    {
      title: "Acceso por QR / Online & Offline",
      image: "https://erp-back.nyc3.digitaloceanspaces.com/assets_public/IMG_4093.jpg",
      features: [
        "Acceso con QR dinámico, invitaciones por email y/o listas dinámicas adaptables a cualquier tipo de entorno"
      ]
    },
    {
      title: "Acceso con Credencial / CI",
      image: "https://erp-back.nyc3.digitaloceanspaces.com/assets_public/IMG_4096.jpg",
      features: [
        "Acceso mediante credenciales corporativas y/o carnet de identidad compatible con ID/IC/Mifare"
      ]
    }
  ];

  return (
    <>
      <SEOHead
        title="Control de Acceso - Cloudbase"
        description="Control de acceso integrado y redefinido. Gestiona usuarios, correlaciona eventos de video vigilancia, integración con Azure AD, acceso móvil y más."
        keywords="control de acceso, seguridad, video vigilancia, Azure AD, acceso móvil, QR, credenciales"
        ogImage="https://erp-back.nyc3.digitaloceanspaces.com/assets_public/Captura-de-pantalla-2025-07-23-a-las-3.45.43-p.-m.png"
      />
      <div className="min-h-screen bg-white">
        <Header />

        {/* Hero Section with Video Background */}
        <section className="pt-32 pb-20 bg-black relative overflow-hidden min-h-screen flex items-center">
          {/* Video background */}
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src="https://erp-back.nyc3.digitaloceanspaces.com/assets_public/access_1.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-black/70"></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-5xl mx-auto text-center">
              <div className="inline-block mb-6 px-4 py-2 bg-green-600/20 border border-green-600/30 text-green-400 rounded-full text-sm font-semibold">
                AccessHub
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8 leading-tight text-white">
                Control de Acceso Inteligente
              </h1>
              
              <p className="text-xl sm:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed font-light">
                Sistema integrado y redefinido. Correlaciona eventos de acceso con video vigilancia, integra con Azure AD y gestiona todo desde una plataforma centralizada.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
                <Button 
                  size="lg" 
                  className="bg-green-600 hover:bg-green-700 text-white px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                  onClick={() => window.location.href = '/demo'}
                >
                  Solicitar Demo
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-black px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                  onClick={() => window.location.href = '/demo'}
                >
                  Ver Funcionalidades
                </Button>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Integración con Azure AD</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Acceso Móvil y QR</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>API Completa</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Video Vigilancia Integrada</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className={`flex flex-col ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } items-center gap-12 mb-20 last:mb-0`}
              >
                <div className="flex-1">
                  <img
                    src={capability.image}
                    alt={capability.title}
                    className="w-full rounded-lg shadow-lg"
                    loading="lazy"
                  />
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    {capability.title}
                  </h2>
                  <ul className="space-y-4">
                    {capability.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg
                          className="w-6 h-6 text-primary mr-3 flex-shrink-0 mt-1"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-gray-700 text-lg">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Management Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Gestiona el control de acceso a escala
            </h2>
            <p className="text-xl text-gray-600 mb-16">
              Obtén información de manera simple para proteger tu empresa
            </p>

            <div className="grid md:grid-cols-3 gap-12">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center text-center"
                >
                  <img
                    src={feature.icon}
                    alt={feature.title}
                    className="w-20 h-20 mb-6 object-contain"
                    loading="lazy"
                  />
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-lg">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Developer Section with Image */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="flex-1">
                <img
                  src="https://erp-back.nyc3.digitaloceanspaces.com/assets_public/Captura-de-pantalla-2025-07-23-a-las-3.43.33-p.-m.png"
                  alt="Developer API"
                  className="w-full rounded-lg shadow-lg"
                  loading="lazy"
                />
              </div>
              <div className="flex-1 text-center lg:text-left">
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Developer friendly
                </h2>
                <p className="text-xl text-gray-600 mb-8">
                  Administra e integra mediante API a cualquiera de tus plataformas.
                  Control total y flexibilidad para desarrolladores.
                </p>
                <Link
                  to="/demo"
                  className="inline-block bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-md text-lg font-semibold transition-colors"
                >
                  Conocer más
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-6">
              ¿Listo para transformar tu control de acceso?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Descubre cómo Cloudbase puede ayudarte a gestionar la seguridad de tu empresa de manera inteligente y eficiente.
            </p>
            <Link
              to="/demo"
              className="inline-block bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-md text-lg font-semibold transition-colors"
            >
              Solicitar Demo
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}

export default AccessControlPage;
